import type { SixesCellOutcome } from "./sixesLiveData";
import { SIXES_MARKET_CELL_COUNT } from "./sixesGridLayout";
import { normalizeSixesBucket, type SixesBucket } from "./sixesMarketBucket";

const STORAGE_KEY_PREFIX = "sixes:settlement-seen:";

export type SixesSeenFooterStats = {
    wins: number;
    slides: number;
    natural: number;
    coins: number;
    multiplierSixes: number;
};

export const EMPTY_SIXES_SEEN_FOOTER_STATS: SixesSeenFooterStats = {
    wins: 0,
    slides: 0,
    natural: 0,
    coins: 0,
    multiplierSixes: 0,
};

export type SixesSeenSettlementSnapshot = {
    v: 2;
    /** Pink-phase outcomes last time the user saw the card (wins stored as win-pink). */
    outcomes: SixesCellOutcome[];
    celebratedLineKeys: string[];
    celebratedBuckets: SixesBucket[];
    /** Footer totals last time the user saw live stat animations. */
    footerStats?: SixesSeenFooterStats;
    /** Bucket colours per market cell — powers the lobby mini-grid preview. */
    cellColors?: string[];
    /** Per-cell bucket assignments — restores slide layout in the lobby mini-grid. */
    buckets?: (SixesBucket | null)[];
};

export function emptySixesSeenPinkOutcomes(): SixesCellOutcome[] {
    return Array.from({ length: SIXES_MARKET_CELL_COUNT }, () => "pending" as SixesCellOutcome);
}

function storageKey(matchId: string): string {
    return `${STORAGE_KEY_PREFIX}${String(matchId || "").trim()}`;
}

export function readSixesSeenSettlement(matchId: string): SixesSeenSettlementSnapshot | null {
    if (typeof window === "undefined") return null;
    const id = String(matchId || "").trim();
    if (!id) return null;
    try {
        const raw = window.localStorage.getItem(storageKey(id));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as SixesSeenSettlementSnapshot & { v?: number };
        const version = parsed?.v === 2 ? 2 : parsed?.v === 1 ? 1 : null;
        if (!version || !Array.isArray(parsed.outcomes)) return null;
        if (parsed.outcomes.length !== SIXES_MARKET_CELL_COUNT) return null;
        const legacySets =
            version === 2 &&
            parsed.footerStats &&
            typeof parsed.footerStats === "object" &&
            "sets" in parsed.footerStats
                ? Math.max(0, Math.round(Number((parsed.footerStats as { sets?: number }).sets) || 0))
                : 0;
        const footerStats =
            version === 2 && parsed.footerStats && typeof parsed.footerStats === "object"
                ? {
                      wins: Math.max(0, Math.round(Number(parsed.footerStats.wins) || 0)),
                      slides: Math.max(
                          0,
                          Math.round(Number(parsed.footerStats.slides ?? legacySets) || 0),
                      ),
                      natural: Math.max(0, Math.round(Number(parsed.footerStats.natural) || 0)),
                      coins: Math.max(0, Math.round(Number(parsed.footerStats.coins) || 0)),
                      multiplierSixes: Math.max(
                          0,
                          Math.round(Number(parsed.footerStats.multiplierSixes) || 0),
                      ),
                  }
                : { ...EMPTY_SIXES_SEEN_FOOTER_STATS };
        const cellColors = Array.isArray(parsed.cellColors)
            ? parsed.cellColors.map((c) => String(c || "").trim()).filter(Boolean)
            : undefined;
        const buckets = Array.isArray(parsed.buckets)
            ? parsed.buckets.map((bucket) =>
                  typeof bucket === "string" ? normalizeSixesBucket(bucket) : null,
              )
            : undefined;
        const celebratedBuckets = Array.isArray(parsed.celebratedBuckets)
            ? parsed.celebratedBuckets
                  .map((bucket) => normalizeSixesBucket(bucket))
                  .filter((bucket): bucket is SixesBucket => bucket != null)
            : [];
        return {
            v: 2,
            outcomes: parsed.outcomes,
            celebratedLineKeys: Array.isArray(parsed.celebratedLineKeys) ? parsed.celebratedLineKeys : [],
            celebratedBuckets,
            footerStats,
            cellColors:
                cellColors && cellColors.length === SIXES_MARKET_CELL_COUNT ? cellColors : undefined,
            buckets: buckets && buckets.length === SIXES_MARKET_CELL_COUNT ? buckets : undefined,
        };
    } catch {
        return null;
    }
}

export function readSixesSeenFooterStats(matchId: string): SixesSeenFooterStats {
    return readSixesSeenSettlement(matchId)?.footerStats ?? { ...EMPTY_SIXES_SEEN_FOOTER_STATS };
}

export function writeSixesSeenSettlement(matchId: string, snapshot: SixesSeenSettlementSnapshot): void {
    if (typeof window === "undefined") return;
    const id = String(matchId || "").trim();
    if (!id) return;
    try {
        window.localStorage.setItem(storageKey(id), JSON.stringify(snapshot));
    } catch {
        /* quota / private mode */
    }
}

export function resetSixesSeenSettlement(matchId: string): void {
    if (typeof window === "undefined") return;
    const id = String(matchId || "").trim();
    if (!id) return;
    window.localStorage.removeItem(storageKey(id));
}

export function pinkOutcomesSnapshotEqual(
    a: readonly SixesCellOutcome[],
    b: readonly SixesCellOutcome[],
): boolean {
    if (a.length !== b.length) return false;
    return a.every((value, idx) => value === b[idx]);
}
